<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Transaction Management' : 'Deleted Transactions'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all financial transactions' : 'Restore or permanently delete transactions'"
            >
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Transactions</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- User Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.user_id"
                    :items="userFilterItems"
                    :item-text="getUserDisplayText"
                    item-value="id"
                    :filter="filterUserByPhone"
                    :loading="mastersLoading"
                    :placeholder="mastersLoading ? 'Loading...' : ''"
                    :no-data-text="mastersLoading ? 'Loading users...' : 'No data available'"
                    label="User"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  >
                    <template #item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.user_name }}
                          <span v-if="item.position" class="grey--text text--darken-1 text-caption ml-1">({{ item.position.name || item.position }})</span>
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="item.phone" class="text-caption">
                          {{ item.phone }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Type Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.transaction_type"
                    :items="typeOptions"
                    label="Type"
                    prepend-inner-icon="mdi-swap-horizontal"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statusOptions"
                    label="Status"
                    prepend-inner-icon="mdi-check-circle"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Transaction Purpose Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.transaction_purpose_id"
                    :items="transactionPurposes"
                    item-text="description"
                    item-value="id"
                    :loading="mastersLoading"
                    :no-data-text="mastersLoading ? 'Loading...' : 'No data available'"
                    label="Transaction Purpose"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Transaction Method Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.method_id"
                    :items="transactionMethods"
                    item-text="description"
                    item-value="id"
                    :loading="mastersLoading"
                    :no-data-text="mastersLoading ? 'Loading...' : 'No data available'"
                    label="Transaction Method"
                    prepend-inner-icon="mdi-credit-card-outline"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Transactions Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Transactions List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-currency-inr</v-icon>
                      {{ count }}
                      {{ count === 1 ? "Transaction" : "Transactions" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && transactions.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="exportToExcel"
                      class="px-2"
                      :loading="isExporting"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading transactions..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="transactions"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- User Info Column -->
                <template #[`item.user_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="primary">
                      <v-icon color="white" size="18">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ item.user ? item.user.user_name : "Unknown User" }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.user && item.user.position ? item.user.position : "No Position" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Transaction Type Column -->
                <template #[`item.transaction_type`]="{ item }">
                  <v-chip
                    :color="item.transaction_type ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.transaction_type ? "mdi-plus" : "mdi-minus" }}
                    </v-icon>
                    {{ item.transaction_type ? "Credit" : "Debit" }}
                  </v-chip>
                </template>

                <!-- Amount Column -->
                <template #[`item.amount`]="{ item }">
                  <div class="text-body-2 font-weight-bold">
                    {{ formatAmount(item.amount) }}
                  </div>
                </template>

                <!-- Status Column -->
                <template #[`item.status`]="{ item }">
                  <v-chip :color="item.status ? 'success' : 'error'" small dark>
                    <v-icon left x-small>
                      {{ item.status ? "mdi-check" : "mdi-close" }}
                    </v-icon>
                    {{ item.status ? "Success" : "Failed" }}
                  </v-chip>
                </template>

                <!-- Transaction Details Column -->
                <!-- Remarks Column -->
                <template #[`item.remarks`]="{ item }">
                  <div class="text-caption grey--text text--darken-1" style="max-width:260px; white-space:normal; line-height:1.4;">
                    {{ item.remarks || '—' }}
                  </div>
                </template>

                <!-- Ref ID Column (Order + Payment ID + Razorpay status) -->
                <template #[`item.transaction_details`]="{ item }">
                  <div>
                    <div v-if="item.order_id" class="text-caption mb-1">
                      <span class="font-weight-medium grey--text">Order:</span>
                      <span class="text--darken-2"> {{ item.order_id }}</span>
                    </div>
                    <div v-if="item.payment_id" class="text-caption mb-1">
                      <span class="font-weight-medium grey--text">Pay:</span>
                      <span class="text--darken-2"> {{ item.payment_id }}</span>
                    </div>
                    <v-chip v-if="item.razorpay_status" x-small :color="item.razorpay_status === 'captured' ? 'success' : item.razorpay_status === 'failed' ? 'error' : 'grey'" dark class="mt-1">
                      {{ item.razorpay_status }}
                    </v-chip>
                    <span v-if="!item.order_id && !item.payment_id" class="text-caption grey--text">—</span>
                  </div>
                </template>

                <!-- Purpose Column -->
                <template #[`item.purpose`]="{ item }">
                  <v-chip x-small outlined :color="item.transaction_purpose && item.transaction_purpose.code === 'RECHARGE' ? 'blue' : item.transaction_purpose && item.transaction_purpose.code === 'CONSULTATION' ? 'purple' : 'grey'">
                    {{ item.transaction_purpose ? item.transaction_purpose.description : 'N/A' }}
                  </v-chip>
                </template>

                <!-- Method Column -->
                <template #[`item.method_info`]="{ item }">
                  <v-chip v-if="item.method" x-small outlined color="teal">
                    {{ item.method.description }}
                  </v-chip>
                  <span v-else class="grey--text text--lighten-1">—</span>
                </template>

                <!-- Active Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/transactions/${encryptIt(item.id)}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Transaction</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Transaction</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="restoreTransaction(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Transaction</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-currency-inr</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Transactions Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No transactions match your current filters. Try adjusting your search criteria."
                          : "You haven't added any transactions yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/transactions/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Transaction
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_transaction" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Transaction deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_transaction = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_transaction" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Transaction restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_transaction = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this transaction?
              </h3>

              <div
                class="transaction-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-currency-inr</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{
                        transactionToDelete
                          ? formatAmount(transactionToDelete.amount)
                          : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      {{
                        transactionToDelete
                          ? transactionToDelete.user
                            ? transactionToDelete.user.user_name
                            : "Unknown User"
                          : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      {{
                        transactionToDelete
                          ? transactionToDelete.transaction_id ||
                            "No Transaction ID"
                          : ""
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the transaction
                to the trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted transactions section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Transaction
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "TransactionsIndex",
  data() {
    return {
      filter: {
        search_keyword: "",
        user_id: "",
        transaction_type: "",
        status: "",
        is_active: "",
        transaction_purpose_id: "",
        method_id: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      showDeleteDialog: false,
      transactionToDelete: null,
      isDeleting: false,
      transactions: [],
      userFilterItems: [],
      transactionPurposes: [],
      transactionMethods: [],
      is_deleted_transaction: false,
      is_restored_transaction: false,
      isLoading: false,
      mastersLoading: false,
      isExporting: false,

      // Filter options
      activeOptions: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],

      typeOptions: [
        { text: "Credit", value: "1" },
        { text: "Debit", value: "0" },
      ],

      statusOptions: [
        { text: "Success", value: "1" },
        { text: "Failed", value: "0" },
      ],

      headers: [
        { text: "#",        value: "sr_no",            sortable: false, width: "50px",  align: "center" },
        { text: "User",     value: "user_info",         sortable: false, width: "180px" },
        { text: "Type",     value: "transaction_type",  sortable: false, width: "90px"  },
        { text: "Amount",   value: "amount",            sortable: false, width: "100px" },
        { text: "Purpose",  value: "purpose",           sortable: false, width: "110px" },
        { text: "Method",   value: "method_info",        sortable: false, width: "100px" },
        { text: "Remarks",  value: "remarks",           sortable: false, width: "260px" },
        { text: "Status",   value: "status",            sortable: false, width: "95px"  },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filter.user_id ||
        this.filter.transaction_type ||
        this.filter.status ||
        this.filter.transaction_purpose_id ||
        this.filter.method_id
      );
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getMasters();
  },

  methods: {
    async getData() {
      console.log("Get Transactions Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      // Ensure all filter values are properly set
      this.filter.user_id = this.filter.user_id || "";
      this.filter.transaction_type = this.filter.transaction_type || "";
      this.filter.status = this.filter.status || "";
      this.filter.transaction_purpose_id = this.filter.transaction_purpose_id || "";
      this.filter.method_id = this.filter.method_id || "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&transaction_type=" +
        this.filter.transaction_type +
        "&status=" +
        this.filter.status +
        "&user_id=" +
        this.filter.user_id +
        "&transaction_purpose_id=" +
        this.filter.transaction_purpose_id +
        "&method_id=" +
        this.filter.method_id;

      await axios
        .get(`transactions?${query}`)
        .then((response) => {
          this.transactions = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    async getMasters() {
      this.mastersLoading = true;
      try {
        const response = await axios.get("masters?context=transactions");

        if (response.data?.users) {
          this.userFilterItems = response.data.users;
        }
        if (response.data?.transaction_purposes) {
          this.transactionPurposes = response.data.transaction_purposes;
        }
        if (response.data?.transaction_methods) {
          this.transactionMethods = response.data.transaction_methods;
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      } finally {
        this.mastersLoading = false;
      }
    },

    getUserDisplayText(user) {
      const pos = user.position?.name || (typeof user.position === 'string' ? user.position : '');
      return pos ? `${user.user_name} (${pos})` : user.user_name;
    },

    filterUserByPhone(item, queryText) {
      const q = queryText.toLowerCase();
      const name = String(item.user_name || '').toLowerCase();
      const phone = String(item.phone || '').toLowerCase();
      const pos = String(item.position?.name || item.position || '').toLowerCase();
      return name.includes(q) || phone.includes(q) || pos.includes(q);
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },

    formatAmount(amount) {
      if (!amount) return "₹0";
      return "₹" + new Intl.NumberFormat("en-IN").format(amount);
    },

    showDeleteConfirmation(transaction) {
      this.transactionToDelete = transaction;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.transactionToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.transactionToDelete) return;

      this.isDeleting = true;

      await axios
        .post(
          `/transactions/delete/${this.encryptIt(this.transactionToDelete.id)}`
        )
        .then(() => {
          this.showDeleteDialog = false;
          this.transactionToDelete = null;
          this.isDeleting = false;
          this.is_deleted_transaction = true;
          this.getData();
        })
        .catch((error) => {
          console.log(error);
          this.isDeleting = false;
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async deleteTransaction(transaction) {
      await axios
        .post(`/transactions/delete/${this.encryptIt(transaction.id)}`)
        .then(() => {
          this.is_deleted_transaction = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async restoreTransaction(transaction) {
      await axios
        .post(`/transactions/restore/${this.encryptIt(transaction.id)}`)
        .then(() => {
          this.is_restored_transaction = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async exportToExcel() {
      this.isExporting = true;

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&transaction_type=" +
        this.filter.transaction_type +
        "&status=" +
        this.filter.status +
        "&user_id=" +
        this.filter.user_id +
        "&transaction_purpose_id=" +
        (this.filter.transaction_purpose_id || "") +
        "&method_id=" +
        (this.filter.method_id || "");

      try {
        const response = await axios.get(`transactions/excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `transactions_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }

      this.isExporting = false;
    },

    clearAllFilters() {
      this.filter.user_id = "";
      this.filter.transaction_type = "";
      this.filter.status = "";
      this.filter.transaction_purpose_id = "";
      this.filter.method_id = "";
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.user_id) count++;
      if (this.filter.transaction_type) count++;
      if (this.filter.status) count++;
      if (this.filter.transaction_purpose_id) count++;
      if (this.filter.method_id) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Delete Confirmation Dialog Styling */
.transaction-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.transaction-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>